// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshTimeoutRef = useRef(null);
  // Promise da renovação em curso (null quando não há nenhuma).
  // Guardamos a promise em vez de um booleano para que chamadas concorrentes
  // possam aguardar o mesmo refresh em vez de receberem "falhou".
  const refreshPromiseRef = useRef(null);

  const refreshAccessTokenRef = useRef(null); // NEW: ref para quebrar a dependência circular



  // Configurar base URL da API
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      axios.defaults.baseURL = 'http://localhost:5000';
    } else {
      axios.defaults.baseURL = window.location.origin;
    }
    
    axios.defaults.timeout = 3000000; 
    console.log('API baseURL configured:', axios.defaults.baseURL);
  }, []);

  // Função para logout (definida antes para evitar dependência circular)
  const logout = useCallback((onLogoutCallback) => {
    // Cancelar renovação automática
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    
    if (typeof onLogoutCallback === 'function') {
      onLogoutCallback();
    }
  }, []);

    // Agendar renovação automática do token
  const scheduleTokenRefresh = useCallback(() => {
    // Cancelar timeout anterior se existir
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // Renovar 5 minutos antes de expirar (25 minutos)
    refreshTimeoutRef.current = setTimeout(() => {
      if (refreshAccessTokenRef.current) {
        refreshAccessTokenRef.current();
      }
    }, 25 * 60 * 1000); // 25 minutos
  }, []); 

  // Função para renovar token
  const refreshAccessToken = useCallback(async () => {
    // Já existe uma renovação a decorrer: devolver a MESMA promise para que
    // quem chamou aguarde o resultado real em vez de assumir que falhou.
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      return false;
    }

    const inFlight = (async () => {
      try {
        const response = await axios.post('/api/auth/refresh', {
          refreshToken
        });

        if (response.data?.success && response.data.token) {
          const { token, user } = response.data;
          localStorage.setItem('token', token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          setUser(user);

          scheduleTokenRefresh();

          return true;
        }

        // 200 sem payload válido - falha silenciosa que antes devolvia undefined
        console.error('Refresh token: resposta sem token válido', response.data);
        return false;
      } catch (error) {
        console.error('Failed to refresh token:', error);
        // Se falhar, fazer logout
        logout();
        return false;
      } finally {
        if (refreshPromiseRef.current === inFlight) {
          refreshPromiseRef.current = null;
        }
      }
    })();

    refreshPromiseRef.current = inFlight;
    return inFlight;
  }, [logout, scheduleTokenRefresh]);

  useEffect(() => {
    refreshAccessTokenRef.current = refreshAccessToken;
    return () => {
      refreshAccessTokenRef.current = null;
    };
  }, [refreshAccessToken]);


  // Interceptor para renovar token automaticamente em caso de 403
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Nunca tentar renovar a partir do próprio endpoint de refresh
        if (originalRequest?.url?.includes('/api/auth/refresh')) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          const refreshed = await refreshAccessToken();
          if (refreshed) {
            // Retentar a requisição original com o novo token
            originalRequest.headers['Authorization'] = `Bearer ${localStorage.getItem('token')}`;
            return axios(originalRequest);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [refreshAccessToken]);

  // Validação inicial do token
  useEffect(() => {
    const validateToken = async () => {
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!token) {
        // Se não tem access token mas tem refresh token, tentar renovar
        if (refreshToken) {
          await refreshAccessToken();
        }
        setLoading(false);
        return;
      }

      try {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await axios.get('/api/auth/validate');
        setUser(response.data.user);
        
        // Agendar renovação do token
        scheduleTokenRefresh();
      } catch (error) {
        console.error('Token validation failed:', error);
        // Tentar renovar com refresh token
        if (refreshToken) {
          const refreshed = await refreshAccessToken();
          if (!refreshed) {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            delete axios.defaults.headers.common['Authorization'];
            setUser(null);
          }
        } else {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [refreshAccessToken, scheduleTokenRefresh]);

  const login = useCallback(async (email, password) => {
    try {
      const response = await axios.post('/api/auth/login', {
        email,
        password
      });
      
      const { token, refreshToken, user } = response.data;
      
      localStorage.setItem('token', token);
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);
      
      // Agendar renovação do token
      scheduleTokenRefresh();
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao fazer login'
      };
    }
  }, [scheduleTokenRefresh]);

  // Abre a sessão a partir de tokens já emitidos pelo servidor (entrada pelo SedLex)
  const loginWithSession = useCallback(async ({ token, refreshToken }) => {
    try {
      localStorage.setItem('token', token);
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      } else {
        localStorage.removeItem('refreshToken');
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.get('/api/auth/validate');
      setUser(response.data.user);

      scheduleTokenRefresh();

      return { success: true };
    } catch (error) {
      console.error('Session login error:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao iniciar sessão'
      };
    }
  }, [scheduleTokenRefresh]);

  const value = {
    user,
    login,
    loginWithSession,
    logout,
    loading,
    refreshAccessToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};